import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Github as GithubIcon, Plus, Trash2, Save } from "lucide-react";
import { BridgeApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { PageBody, PageHeader } from "../components/PageScaffold";
import { Button, Pill, EmptyState } from "../components/primitives";
import { cn } from "../lib/cn";
import type { GithubRepo } from "../lib/types";

export default function Github() {
  const qc = useQueryClient();
  const { data: conn } = useQuery({ queryKey: ["github", "connection"], queryFn: BridgeApi.githubConnection });
  const { data: reposData, isLoading } = useQuery({ queryKey: ["github", "repos"], queryFn: BridgeApi.githubRepos });
  const { data: cfg } = useQuery({ queryKey: qk.bridge.config, queryFn: BridgeApi.config });
  const personaNames = cfg ? Object.keys(cfg.personas) : [];
  const repos = reposData?.repos ?? [];
  const connected = !!conn?.channel;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["github", "repos"] });
    qc.invalidateQueries({ queryKey: ["github", "connection"] });
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="GitHub"
        icon={GithubIcon}
        subtitle={connected ? `${conn!.channel!.owner} · App ${conn!.app_id ?? "?"} · ${conn!.channel!.status}` : "connect a GitHub App to let personas act on issues/PRs"}
      />
      <PageBody>
        <ConnectionPanel conn={conn} onSaved={invalidate} />
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-fg">Repositories</h2>
            {connected && <AddRepo defaultOwner={conn!.channel!.owner ?? ""} personaNames={personaNames} onSaved={invalidate} />}
          </div>
          {!connected ? (
            <EmptyState icon={<GithubIcon className="size-8" />} title="Not connected" description="Connect the GitHub App above, then add repositories." />
          ) : isLoading ? (
            <p className="text-xs text-fg-subtle">Loading…</p>
          ) : repos.length === 0 ? (
            <EmptyState icon={<GithubIcon className="size-8" />} title="No repositories" description="Add a repo to configure which personas act on it." />
          ) : (
            <div className="space-y-3">
              {repos.map((r) => <RepoEditor key={r.id} repo={r} personaNames={personaNames} onSaved={invalidate} />)}
            </div>
          )}
        </div>
      </PageBody>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs text-fg-muted mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-fg-subtle mt-0.5">{hint}</span>}
    </label>
  );
}

function ConnectionPanel({ conn, onSaved }: { conn?: import("../lib/types").GithubConnection; onSaved: () => void }) {
  const toast = useToast();
  const [owner, setOwner] = useState("");
  const [appId, setAppId] = useState("");
  const [keyPath, setKeyPath] = useState("");
  const [secret, setSecret] = useState("");
  const [installId, setInstallId] = useState("");
  useEffect(() => {
    if (conn?.channel) { setOwner(conn.channel.owner ?? ""); setAppId(conn.app_id ?? ""); setInstallId(conn.installation_id ? String(conn.installation_id) : ""); }
  }, [conn]);

  const save = useMutation({
    mutationFn: () => BridgeApi.saveGithubConnection({
      owner, app_id: appId, private_key_path: keyPath,
      webhook_secret: secret || undefined,
      installation_id: installId ? Number(installId) : undefined,
    }),
    onSuccess: () => { toast.success("GitHub connection saved"); onSaved(); },
    onError: (e) => toast.error("Save failed", String(e)),
  });
  // Enable/disable the whole connection without deleting its config. When off,
  // the bridge acks incoming webhooks but spawns nothing.
  const toggleEnabled = useMutation({
    mutationFn: (enabled: boolean) => BridgeApi.setGithubConnectionEnabled(enabled),
    onSuccess: (r) => { toast.success(`GitHub connection ${r.enabled ? "enabled" : "disabled"}`); onSaved(); },
    onError: (e) => toast.error("Toggle failed", String(e)),
  });

  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-fg">Connection (GitHub App)</h2>
        {conn?.channel && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-fg-muted" title="Disable to stop the fleet acting on GitHub without losing this config">
              <input
                type="checkbox" className="size-3.5"
                checked={conn.channel.enabled}
                disabled={toggleEnabled.isPending}
                onChange={(e) => toggleEnabled.mutate(e.target.checked)}
              />
              Enabled
            </label>
            <Pill tone={conn.channel.status === "connected" ? "success" : "warning"}>{conn.channel.status}</Pill>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Org / owner"><input className="w-full" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="qdhayana" /></Field>
        <Field label="App ID"><input className="w-full" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="4283953" /></Field>
        <Field label="Private key path" hint="absolute path to the App .pem on the host (0600)"><input className="w-full font-mono text-xs" value={keyPath} onChange={(e) => setKeyPath(e.target.value)} placeholder="/home/putin/.config/github-app/qdhayana.pem" /></Field>
        <Field label="Installation ID" hint="blank = auto-discover"><input className="w-full" value={installId} onChange={(e) => setInstallId(e.target.value)} placeholder="146203307" /></Field>
        <Field label={conn?.has_webhook_secret ? "Webhook secret (set — leave blank to keep)" : "Webhook secret"}><input className="w-full font-mono text-xs" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="•••" /></Field>
        <Field label="Webhook URL (register on the App)"><input className="w-full font-mono text-xs" readOnly value={conn?.webhook_url ?? ""} /></Field>
      </div>
      <div className="mt-3">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}><Save className="size-3.5" />Save connection</Button>
        {conn && <span className="ml-3 text-[11px] text-fg-subtle">key on disk: {conn.has_key ? "yes" : "no"}</span>}
      </div>
    </div>
  );
}


function AddRepo({ defaultOwner, personaNames, onSaved }: { defaultOwner: string; personaNames: string[]; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  if (!open) return <Button size="sm" variant="secondary" onClick={() => setOpen(true)}><Plus className="size-3.5" />Add repo</Button>;
  return <RepoEditor personaNames={personaNames} defaultOwner={defaultOwner} onSaved={() => { setOpen(false); onSaved(); }} onCancel={() => setOpen(false)} isNew />;
}

function RepoEditor({ repo, personaNames, defaultOwner, onSaved, onCancel, isNew }: {
  repo?: GithubRepo; personaNames: string[]; defaultOwner?: string; onSaved: () => void; onCancel?: () => void; isNew?: boolean;
}) {
  const toast = useToast();
  const [owner, setOwner] = useState(repo?.owner ?? defaultOwner ?? "");
  const [name, setName] = useState(repo?.repo ?? "");
  const [enabled, setEnabled] = useState(repo ? repo.enabled === 1 : true);
  const [instructions, setInstructions] = useState(repo?.instructions ?? "");
  const [trusted, setTrusted] = useState(repo?.trusted_users ? (JSON.parse(repo.trusted_users) as string[]).join(", ") : "");
  const [baseBranch, setBaseBranch] = useState(repo?.base_branch ?? "main");
  const [maxConcurrent, setMaxConcurrent] = useState(repo?.max_concurrent ?? 2);
  const [pick, setPick] = useState("");
  // Repos the App can actually act on — only needed when adding a new one.
  const { data: avail } = useQuery({ queryKey: ["github-available-repos"], queryFn: () => BridgeApi.githubAvailableRepos(), enabled: !!isNew, staleTime: 60_000 });
  const available = avail?.repos ?? [];
  // Allowlist: persona -> optional role note. Presence = allowed to be @-mentioned.
  // Every entry is a `mention` binding under the hood (no per-trigger config —
  // a mentioned persona self-directs across issues + PRs).
  const [allowed, setAllowed] = useState<Record<string, string>>(
    Object.fromEntries((repo?.personas ?? []).map((p) => [p.persona, p.role_note ?? ""])),
  );
  const toggle = (p: string) => setAllowed((a) => { const n = { ...a }; if (p in n) delete n[p]; else n[p] = ""; return n; });
  const setNote = (p: string, note: string) => setAllowed((a) => ({ ...a, [p]: note }));

  const save = useMutation({
    mutationFn: () => BridgeApi.saveGithubRepo({
      owner, repo: name, enabled, instructions: instructions || null,
      trusted_users: trusted.split(",").map((s) => s.trim()).filter(Boolean),
      base_branch: baseBranch, max_concurrent: maxConcurrent,
      personas: Object.entries(allowed).map(([persona, role_note]) => ({ persona, trigger: "mention" as const, role_note: role_note || null, enabled: true })),
    }),
    onSuccess: () => { toast.success(`${owner}/${name} saved`); onSaved(); },
    onError: (e) => toast.error("Save failed", String(e)),
  });
  const del = useMutation({
    mutationFn: () => BridgeApi.deleteGithubRepo(repo!.id),
    onSuccess: () => { toast.success("Repo removed"); onSaved(); },
    onError: (e) => toast.error("Delete failed", String(e)),
  });

  return (
    <div className={cn("panel p-4", isNew && "ring-1 ring-accent/40")}>
      {isNew ? (
        <Field label="Repository" hint="type to search repos the App can access — owner is set automatically">
          <input
            className="w-full font-mono text-xs"
            list="gh-available-repos"
            value={pick}
            onChange={(e) => {
              const v = e.target.value;
              setPick(v);
              const r = available.find((a) => a.full_name === v);
              setOwner(r?.owner ?? "");
              setName(r?.name ?? "");
            }}
            placeholder={available.length ? "qdhayana/…" : "loading accessible repos…"}
          />
          <datalist id="gh-available-repos">
            {available.map((r) => <option key={r.full_name} value={r.full_name}>{r.private ? "private" : "public"}</option>)}
          </datalist>
          {pick && !name && <p className="text-[11px] text-danger mt-0.5">pick a repo from the list</p>}
        </Field>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Owner"><input className="w-full" value={owner} disabled /></Field>
          <Field label="Repo"><input className="w-full" value={name} disabled /></Field>
        </div>
      )}
      <label className="flex items-center gap-2 mt-2 text-xs text-fg-muted">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="size-3.5" /> enabled (fleet acts on this repo)
      </label>
      <div className="mt-3">
        <Field label="Repo instructions" hint="injected into the persona prompt for work on this repo">
          <textarea className="w-full font-sans text-sm" rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Flutter conventions; run melos test before opening a PR." />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3">
        <Field label="Trusted GitHub users" hint="comma-separated logins who may trigger; * = anyone with access (private repos only); empty = nobody"><input className="w-full font-mono text-xs" value={trusted} onChange={(e) => setTrusted(e.target.value)} placeholder="hksitorus, other  (or *)" /></Field>
        <Field label="Base branch"><input className="w-full" value={baseBranch} onChange={(e) => setBaseBranch(e.target.value)} /></Field>
        <Field label="Max concurrent"><input className="w-full" type="number" value={maxConcurrent} onChange={(e) => setMaxConcurrent(Number(e.target.value))} /></Field>
      </div>

      {/* Personas allowed to be @-mentioned */}
      <div className="mt-4">
        <span className="text-xs font-medium text-fg-muted">Personas allowed to be @-mentioned</span>
        <p className="text-[11px] text-fg-subtle mb-1.5">A checked persona acts when @-named in a comment on any issue or PR here (it self-directs — implement / review / reply — and posts as the bot). The optional note is its default lane when the mention gives no explicit instruction.</p>
        <div className="space-y-1.5">
          {personaNames.map((p) => {
            const on = p in allowed;
            return (
              <div key={p} className="flex items-center gap-2">
                <label className="flex items-center gap-1.5 w-36 shrink-0 text-xs">
                  <input type="checkbox" checked={on} onChange={() => toggle(p)} className="size-3.5" /> {p}
                </label>
                {on && <input className="flex-1 h-7 text-xs" value={allowed[p]} onChange={(e) => setNote(p, e.target.value)} placeholder="optional role note — e.g. implement & open a PR / review PRs" />}
              </div>
            );
          })}
        </div>
        {Object.keys(allowed).length === 0 && <p className="text-[11px] text-fg-subtle mt-1">None allowed — this repo won't respond to any mention.</p>}
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!owner || !name}><Save className="size-3.5" />Save</Button>
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>}
        {repo && !isNew && <Button size="sm" variant="ghost" className="ml-auto" onClick={() => { if (confirm(`Remove ${owner}/${name}?`)) del.mutate(); }}><Trash2 className="size-3.5" />Remove</Button>}
      </div>
    </div>
  );
}
